import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { Label } from '@components/atoms/Label';
import { Checkbox } from '@components/atoms/Checkbox';
import {
  RetainingWallCalculatorInputs,
  RetainingWallCalculatorResults,
  OnAddToMaterialCallback,
  OnAddAllMaterialsCallback,
  MaterialFromCalculator,
} from '../../types/calculator.types';
import { Plus } from 'lucide-react';
import { useTranslation } from '@hooks/useTranslation';

interface RetainingWallCalculatorProps {
  onAddToMaterial?: OnAddToMaterialCallback;
  onAddAllMaterials?: OnAddAllMaterialsCallback;
}

const RetainingWallCalculator: React.FC<RetainingWallCalculatorProps> = ({
  onAddToMaterial,
  onAddAllMaterials,
}) => {
  const tCalc = useTranslation('calculators');
  const [inputs, setInputs] = useState<RetainingWallCalculatorInputs>({
    wallLength: '',
    wallHeight: '',
    blockLength: '',
    blockHeight: '',
    hasCapRow: false,
    backfillLength: '',
    backfillHeight: '',
    backfillThickness: '',
  });

  const [results, setResults] =
    useState<RetainingWallCalculatorResults | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Scroll to results when they appear
  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [results]);

  const handleInputChange =
    (field: keyof RetainingWallCalculatorInputs) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === 'checkbox'
          ? event.target.checked
          : event.target.value;
      setInputs((prev) => ({ ...prev, [field]: value }));
    };

  const clearForm = () => {
    setInputs({
      wallLength: '',
      wallHeight: '',
      blockLength: '',
      blockHeight: '',
      hasCapRow: false,
      backfillLength: '',
      backfillHeight: '',
      backfillThickness: '',
    });
    setResults(null);
  };

  const calculateMaterials = () => {
    setResults(null);

    const parsedWallLength = parseFloat(inputs.wallLength);
    const parsedWallHeight = parseFloat(inputs.wallHeight);
    const parsedBlockLength = parseFloat(inputs.blockLength);
    const parsedBlockHeight = parseFloat(inputs.blockHeight);
    const parsedBackfillLength = parseFloat(inputs.backfillLength);
    const parsedBackfillHeight = parseFloat(inputs.backfillHeight);
    const parsedBackfillThickness = parseFloat(inputs.backfillThickness);

    if (
      isNaN(parsedWallLength) ||
      isNaN(parsedWallHeight) ||
      parsedWallLength <= 0 ||
      parsedWallHeight <= 0
    ) {
      return;
    }
    if (
      isNaN(parsedBlockLength) ||
      isNaN(parsedBlockHeight) ||
      parsedBlockLength <= 0 ||
      parsedBlockHeight <= 0
    ) {
      return;
    }
    if (
      isNaN(parsedBackfillLength) ||
      isNaN(parsedBackfillHeight) ||
      isNaN(parsedBackfillThickness) ||
      parsedBackfillLength <= 0 ||
      parsedBackfillHeight <= 0 ||
      parsedBackfillThickness <= 0
    ) {
      return;
    }

    try {
      const wallLengthInches = parsedWallLength * 12;
      const wallHeightInches = parsedWallHeight * 12;

      const blocksPerCourse = Math.ceil(wallLengthInches / parsedBlockLength);
      const numCourses = Math.ceil(wallHeightInches / parsedBlockHeight);

      const calculatedWallBlocks = blocksPerCourse * numCourses;
      const calculatedCapBlocks = inputs.hasCapRow ? blocksPerCourse : 0;

      const combinedTotalBlocks = calculatedWallBlocks + calculatedCapBlocks;

      const backfillThicknessFt = parsedBackfillThickness / 12;

      const backfillVolumeCuFt =
        parsedBackfillLength * parsedBackfillHeight * backfillThicknessFt;

      const calculatedTotalGravel = backfillVolumeCuFt / 27;

      const gravelDensityLbsPerCuYd = 2700;
      const calculatedGravelWeight =
        calculatedTotalGravel * gravelDensityLbsPerCuYd;

      setResults({
        totalBlocks: combinedTotalBlocks,
        totalWallBlocks: calculatedWallBlocks,
        totalCapBlocks: calculatedCapBlocks,
        totalGravel: calculatedTotalGravel.toFixed(2),
        gravelWeight: calculatedGravelWeight.toFixed(2),
      });
    } catch (error) {
      console.error('Calculation failed:', error);
    }
  };

  return (
    <div>
      <h3 className="text-xl font-semibold text-center text-gray-900 dark:text-white mb-6">
        {tCalc.names.retainingWall}
      </h3>

      {/* Wall & Block Dimensions */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.retainingWall.wallBlockDimensions}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label label={tCalc.dimensions.wallLengthFt} htmlFor="wallLength" />
            <Input
              id="wallLength"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 20`}
              value={inputs.wallLength}
              onChange={handleInputChange('wallLength')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.retainingWall.wallHeightFt} htmlFor="wallHeight" />
            <Input
              id="wallHeight"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 4`}
              value={inputs.wallHeight}
              onChange={handleInputChange('wallHeight')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.retainingWall.blockLengthIn} htmlFor="blockLength" />
            <Input
              id="blockLength"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 16`}
              value={inputs.blockLength}
              onChange={handleInputChange('blockLength')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.retainingWall.blockHeightIn} htmlFor="blockHeight" />
            <Input
              id="blockHeight"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 4`}
              value={inputs.blockHeight}
              onChange={handleInputChange('blockHeight')}
              className="mt-1"
            />
          </div>
        </div>

        <div className="mt-4">
          <Checkbox
            checked={inputs.hasCapRow}
            onChange={(checked) =>
              setInputs((prev) => ({ ...prev, hasCapRow: checked }))
            }
            label={tCalc.retainingWall.includeCapRow}
          />
        </div>
      </div>

      {/* Gravel Backfill */}
      <div className="mb-6">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
          {tCalc.retainingWall.gravelBackfill}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label label={tCalc.dimensions.lengthFt} htmlFor="backfillLength" />
            <Input
              id="backfillLength"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 20`}
              value={inputs.backfillLength}
              onChange={handleInputChange('backfillLength')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.dimensions.heightFt} htmlFor="backfillHeight" />
            <Input
              id="backfillHeight"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 3.5`}
              value={inputs.backfillHeight}
              onChange={handleInputChange('backfillHeight')}
              className="mt-1"
            />
          </div>
          <div>
            <Label label={tCalc.dimensions.thicknessIn} htmlFor="backfillThickness" />
            <Input
              id="backfillThickness"
              type="number"
              placeholder={`${tCalc.actions.examplePrefix}, 12`}
              value={inputs.backfillThickness}
              onChange={handleInputChange('backfillThickness')}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-4 mb-6">
        <Button
          fullWidth
          variant="primary"
          size="lg"
          onClick={calculateMaterials}>
          {tCalc.actions.calculate}
        </Button>
        <Button fullWidth variant="tertiary" size="lg" onClick={clearForm}>
          {tCalc.actions.clear}
        </Button>
      </div>

      {results && (
        <div
          ref={resultsRef}
          className="p-4 mb-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border-t-4 border-indigo-600">
          <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
            {tCalc.actions.results}
          </h4>
          <div className="space-y-3">
            {/* Wall Blocks */}
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-semibold">{tCalc.retainingWall.wallBlocks}</span>{' '}
              <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                {results.totalWallBlocks}
              </span>{' '}
              {results.totalWallBlocks === 1
                ? tCalc.retainingWall.blocksUnitOne
                : tCalc.retainingWall.blocksUnitOther}
            </p>

            {/* Cap Blocks (optional) */}
            {inputs.hasCapRow && (
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.retainingWall.capBlocks}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.totalCapBlocks}
                </span>{' '}
                {results.totalCapBlocks === 1
                  ? tCalc.retainingWall.blocksUnitOne
                  : tCalc.retainingWall.blocksUnitOther}
              </p>
            )}

            {/* Total Blocks */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.retainingWall.totalBlocks}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.totalBlocks}
                </span>{' '}
                {results.totalBlocks === 1
                  ? tCalc.retainingWall.blocksUnitOne
                  : tCalc.retainingWall.blocksUnitOther}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: results.totalBlocks,
                      amountType: 'units',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.retainingWall.wallBlocksMaterial,
                      resultLabel: tCalc.retainingWall.totalBlocks,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Gravel Needed */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.retainingWall.gravelNeeded}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.totalGravel}
                </span>{' '}
                {tCalc.dimensions.cubicYards}
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: parseFloat(results.totalGravel),
                      amountType: 'cubicYards',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.retainingWall.gravelBackfill,
                      resultLabel: tCalc.retainingWall.gravelNeeded,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Gravel Weight */}
            <div className="flex items-center justify-between">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-semibold">{tCalc.retainingWall.gravelWeight}</span>{' '}
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {results.gravelWeight}
                </span>{' '}
                lbs
              </p>
              {onAddToMaterial && (
                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() =>
                    onAddToMaterial({
                      amount: parseFloat(results.gravelWeight),
                      amountType: 'pounds',
                      amountSystem: 'imperial',
                      suggestedName: tCalc.materials.gravel,
                      resultLabel: tCalc.retainingWall.gravelWeight,
                    })
                  }>
                  {tCalc.actions.addToMaterial}
                </Button>
              )}
            </div>

            {/* Add All Materials Button */}
            {onAddAllMaterials && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  fullWidth
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => {
                    const allMaterials: MaterialFromCalculator[] = [
                      {
                        amount: results.totalBlocks,
                        amountType: 'units',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.retainingWall.wallBlocksMaterial,
                        resultLabel: tCalc.retainingWall.totalBlocks,
                      },
                      {
                        amount: parseFloat(results.totalGravel),
                        amountType: 'cubicYards',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.retainingWall.gravelBackfill,
                        resultLabel: tCalc.retainingWall.gravelNeeded,
                      },
                      {
                        amount: parseFloat(results.gravelWeight),
                        amountType: 'pounds',
                        amountSystem: 'imperial',
                        suggestedName: tCalc.materials.gravel,
                        resultLabel: tCalc.retainingWall.gravelWeight,
                      },
                    ];
                    onAddAllMaterials(allMaterials);
                  }}>
                  {tCalc.actions.addAllMaterials} (3)
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RetainingWallCalculator;
